import { useEffect, useState } from "react";
import { ISbStoryData } from "@storyblok/js";
import { storyblokEventEmitter } from "@made-people/grease-storyblok";
import { FrontendContextV1 } from "@made-people/nestjs-bff-service";
import {
  useGearsConfigurationVariable,
  useGearsServiceUrl,
} from "@made-people/gears-configuration";
import { BffClient } from "@made-people/gears-bff-client";
import { useRootLoaderData } from "@made-people/gears-remix";

/**
 * Hook to get the frontend context influenced by the storyblok event emitter
 */
export function useFrontendContext() {
  const rootLoaderData = useRootLoaderData();
  const initialFrontendContext =
    rootLoaderData?.settingsAndContentResponse?.frontendContext;

  if (!initialFrontendContext) {
    throw new Error(
      "No frontend context found, this shouldn't happen, please check the root loader data",
    );
  }

  const [frontendContext, setFrontendContext] = useState<FrontendContextV1>(
    initialFrontendContext,
  );

  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");

  useEffect(() => {
    async function storyUpdated(story: ISbStoryData) {
      if (!frontendContext) {
        return;
      }
      const bffClient = new BffClient({ baseUrl: bffUrl, tenantId });
      const newFrontendContext =
        await bffClient.findFrontendContextByCountryAndLanguageCode(
          story.content?.previewCountry || frontendContext.country,
          frontendContext.language, // TODO: get storyblok's selected language code in here somehow
        );

      setFrontendContext(
        newFrontendContext?.frontendContext ?? frontendContext,
      );
    }

    storyblokEventEmitter.on("storyUpdated", storyUpdated);
    return () => {
      storyblokEventEmitter.off("storyUpdated", storyUpdated);
    };
  }, [bffUrl, frontendContext, tenantId]);

  return frontendContext;
}
